import React from 'react'
import classNames from 'classnames'
import { Header, Stack, HTMLText } from '@crossfit/ui'

import type { AffiliateDailyClassPlanContent } from '../types'
import Section from 'pages/Programming/components/Section'
import { List, ListItem } from 'pages/Programming/components/List'
import DailyClassPlanHeader from './Header'
import { useStrings } from 'components/Settings'
import { Timeline, TimelineItem } from './Timeline'
import ScoreTag from 'components/ScoreTag'
import YouTubeVideo from 'components/YouTubeVideo'
import style from './style.module.css'
import WorkoutText from '../components/WorkoutText'
import { trackEvent } from 'services/segment'

function formatMinutes(minutes) {
  return minutes < 10 ? `0${minutes}` : minutes
}

type Props = {
  classPlan: AffiliateDailyClassPlanContent
}

function AffiliateDailyClassPlan({ classPlan }: Props) {
  const strings = useStrings()
  const {
    name,
    type,
    workouts,
    aboutTheWorkout,
    scaling,
    logistics,
    videoResources,
    accessoryWork,
  } = classPlan

  const onCopy = (e) => {
    trackEvent({
      component: 'Affiliate Daily Class plan',
      event: 'Affiliate Daily Class plan Copied',
      location: 'Affiliate Toolkit',
      text: e.target.innerText,
    })
  }

  return (
    <main className={style.main} onCopy={onCopy}>
      <Section className={style.section}>
        <DailyClassPlanHeader name={name} type={type} />

        {workouts && (
          <div className={style.workouts}>
            {workouts.map((workout) => (
              <article key={workout.level}>
                <Header size='medium' as='h3' mb={4}>
                  {workout.level}
                </Header>
                <WorkoutText>{workout.description}</WorkoutText>
                {workout.score && <ScoreTag>{workout.score}</ScoreTag>}
              </article>
            ))}
            {accessoryWork?.map((accessoryWork) => (
              <article key={accessoryWork.title}>
                <Header size='medium' as='h3' mb={4}>
                  {accessoryWork.title}
                </Header>
                <WorkoutText>{accessoryWork.description}</WorkoutText>
              </article>
            ))}
          </div>
        )}
      </Section>

      {aboutTheWorkout && (
        <Section className={style.section}>
          <Header size='large' as='h2' mb={12}>
            {strings.aboutTheWorkoutText}
          </Header>
          <div className={style.content}>
            <div className={style.halfContent}>
              <Header size='small' mb={2} as='h3'>
                {strings.intendedStimulusText}
              </Header>
              <List className={style.sectionList}>
                {aboutTheWorkout.intendedStimulus.map((stimulus) => (
                  <ListItem key={stimulus.stimulus}>
                    {stimulus.stimulus}
                  </ListItem>
                ))}
              </List>

              <Header size='small' mb={2} as='h3'>
                {strings.coachingGoalsText}
              </Header>
              <List className={style.sectionList}>
                {aboutTheWorkout.coachingGoals.map((goal) => (
                  <ListItem key={goal.goal}>{goal.goal}</ListItem>
                ))}
              </List>
            </div>

            <div className={style.halfContent}>
              <Header size='small' mb={6} as='h3'>
                {strings.videoOverviewText}
              </Header>
              <YouTubeVideo
                id={aboutTheWorkout.videoOverview.youtubeId}
                title={aboutTheWorkout.videoOverview.linkText}
              />
            </div>
          </div>
        </Section>
      )}

      {scaling && (
        <Section className={style.section}>
          <Header size='large' mb={12} as='h2'>
            {strings.scalingText}
          </Header>

          <div className={style.content}>
            <div className={style.halfContent}>
              <Header size='small' mb={4} as='h3'>
                {strings.overviewText}
              </Header>
              <HTMLText>{scaling.overview}</HTMLText>
            </div>
            {scaling.movementScalingOptions && (
              <div className={style.halfContent}>
                <Header size='small' mb={4} as='h3'>
                  {strings.movementSubstitutionsText}
                </Header>
                <table className={style.table}>
                  <thead>
                    <tr>
                      <th>{strings.movementText}</th>
                      <th>{strings.substitutionText}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {scaling.movementScalingOptions.map((modification) => (
                      <tr key={modification.movement}>
                        <td className={style.element}>
                          {modification.movement}
                        </td>
                        <td>{modification.substitutions}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </Section>
      )}

      {logistics && (
        <Section className={style.section}>
          <Header size='large' mb={12} as='h2'>
            {strings.logisticsText}
          </Header>
          <div className={style.content}>
            <div className={style.halfContent}>
              <Header size='small' mb={4} as='h3'>
                {strings.alternateWorkoutText}
              </Header>
              <HTMLText>{logistics.limitations}</HTMLText>
            </div>

            <div className={style.halfContent}>
              <Header size='small' mb={4} as='h3'>
                {strings.otherConsiderationsText}
              </Header>
              <HTMLText>{logistics.otherConsiderations}</HTMLText>
            </div>
          </div>
        </Section>
      )}

      {videoResources && (
        <Section color='black'>
          <Header size='large' mb={12} as='h2'>
            {strings.videoResourcesText}
          </Header>
          <div className={style.demoVideos}>
            {videoResources.map((video) => (
              <YouTubeVideo
                key={video.title}
                id={video.youtubeId}
                title={video.title}
              />
            ))}
          </div>
        </Section>
      )}

      {classPlan.classPlan && (
        <Section className={style.section}>
          <Header size='xlarge' mb={12} as='h2'>
            {strings.classPlanText}
          </Header>
          <div className={style.classPlanContent}>
            <Timeline className={style.timeline}>
              {classPlan.classPlan.map(({ title, duration, hourTime }) => (
                <TimelineItem key={title} title={title} duration={duration}>
                  :{formatMinutes(hourTime)}
                </TimelineItem>
              ))}
            </Timeline>
            <div className={style.classPlanSections}>
              {classPlan.classPlan.map((item) => (
                <section
                  key={item.title}
                  className={classNames(
                    style.classPlanSection,
                    style[item.title],
                  )}
                >
                  <Stack
                    direction={['horizontal', 'vertical']}
                    spacing={[4, 2]}
                    mb={8}
                    className={style.classPlanHeading}
                  >
                    <Header
                      size='large'
                      as='h3'
                      className={style.classPlanHeader}
                    >
                      {strings[`${item.title}Text`] || item.title}
                    </Header>
                    <Header as='p' size='xxsmall' className={style.minutes}>
                      ({item.duration} minutes)
                    </Header>
                  </Stack>

                  {item.components?.map((component, index) => {
                    switch (component.acfFcLayout) {
                      case 'wysiwyg':
                        return (
                          <section
                            key={index}
                            className={style.classPlanComponent}
                          >
                            <Header size='small' mb={1} as='h4'>
                              {component.title}
                            </Header>
                            <HTMLText>{component.body}</HTMLText>
                          </section>
                        )
                        break
                      case 'at_a_glance':
                        return (
                          <section
                            key={index}
                            className={classNames(
                              style.classPlanComponent,
                              style.atAGlance,
                            )}
                          >
                            <Header size='small' mb={4} as='h4'>
                              {component.title}
                            </Header>
                            <List>
                              {component.items.map((item, index) => (
                                <ListItem key={index}>{item.item}</ListItem>
                              ))}
                            </List>
                          </section>
                        )
                      case 'table':
                        return (
                          <table
                            key={index}
                            className={classNames(
                              style.classPlanComponent,
                              style.table,
                            )}
                          >
                            <thead>
                              <tr>
                                {component.table.header.map((item, index) => (
                                  <th key={index}>{item.c}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {component.table.body.map((row, index) => (
                                <tr key={index}>
                                  {row.map((item, index) => (
                                    <td key={index}>{item.c}</td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )
                    }
                  })}
                </section>
              ))}
            </div>
          </div>
        </Section>
      )}
    </main>
  )
}

export default AffiliateDailyClassPlan
